// 認証後の共通レイアウト（ジョーシス風: 左サイドバーナビ + ヘッダ + メイン）。
// 本格的な共通UIパッケージ @cloud-checker/ui は別サブプロジェクト。それまでの最小ローカルシェル。
// WCAG2: nav landmark / aria-current=page / ページ見出し h1 はヘッダが単一で保持 / 可視フォーカス。

import { notifications } from "../ui/notification-store";
import { createNotificationBell } from "../ui/notification-bell";

export type ShellRoute = "dashboard" | "sf-audit" | "gw-audit" | "tenant-admin";

export interface AppShellDeps {
  readonly active: ShellRoute;
  readonly title: string;
  readonly onNavigate: (route: ShellRoute) => void;
  readonly onLogout: () => void;
  readonly headerActions?: readonly HTMLElement[];
}

interface NavItem {
  readonly route: ShellRoute;
  readonly label: string;
}

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { route: "dashboard", label: "ダッシュボード" },
  { route: "sf-audit", label: "Salesforce 監査" },
  { route: "gw-audit", label: "Google Workspace 監査" },
  { route: "tenant-admin", label: "テナント管理" },
];

const NAV_BASE = "block w-full rounded px-3 py-2 text-left text-sm font-medium";
const NAV_ACTIVE = NAV_BASE + " bg-shinonome-50 text-shinonome-700";
const NAV_IDLE = NAV_BASE + " text-slate-700 hover:bg-slate-100";

export function createAppShell(deps: AppShellDeps): { shell: HTMLElement; content: HTMLElement } {
  const shell = document.createElement("div");
  shell.className = "flex min-h-screen bg-white text-slate-900";

  const sidebar = document.createElement("nav");
  sidebar.setAttribute("aria-label", "メインナビゲーション");
  sidebar.className = "flex w-60 flex-col border-r border-slate-200 bg-slate-50";

  const brand = document.createElement("div");
  brand.className = "px-5 py-4 text-lg font-semibold text-shinonome-700";
  brand.textContent = "ソラミエ";

  const list = document.createElement("ul");
  list.className = "flex-1 space-y-1 px-2 py-2";

  for (const item of NAV_ITEMS) {
    const li = document.createElement("li");
    const btn = document.createElement("button");
    btn.type = "button";
    const isActive = item.route === deps.active;
    btn.className = isActive ? NAV_ACTIVE : NAV_IDLE;
    btn.textContent = item.label;
    if (isActive) {
      btn.setAttribute("aria-current", "page");
    }
    btn.addEventListener("click", () => deps.onNavigate(item.route));
    li.append(btn);
    list.append(li);
  }

  const logoutWrap = document.createElement("div");
  logoutWrap.className = "border-t border-slate-200 p-2";
  const logout = document.createElement("button");
  logout.type = "button";
  logout.className = NAV_IDLE;
  logout.textContent = "ログアウト";
  logout.addEventListener("click", () => deps.onLogout());
  logoutWrap.append(logout);

  sidebar.append(brand, list, logoutWrap);

  const column = document.createElement("div");
  column.className = "flex flex-1 flex-col";

  const header = document.createElement("header");
  header.className = "flex items-center justify-between border-b border-slate-200 px-6 py-4";
  const title = document.createElement("h1");
  title.className = "text-xl font-semibold text-slate-900";
  title.textContent = deps.title;
  const actions = document.createElement("div");
  actions.className = "flex items-center gap-2";
  for (const a of deps.headerActions ?? []) actions.append(a);
  actions.append(createNotificationBell(notifications)); // ベルは全認証画面に常設(右端)
  header.append(title, actions);

  const content = document.createElement("main");
  content.className = "flex-1 px-6 py-6";

  column.append(header, content);
  shell.append(sidebar, column);

  return { shell, content };
}
